import { ColDef, IDateFilterParams } from 'ag-grid-community';
import moment from 'moment';

/**
 * Shared layout for AG Grid date columns with floating date filters.
 * Fixed width (not flex-grow) so date filters stay readable without
 * crushing neighboring columns.
 */
export const AG_GRID_DATE_COLUMN_WIDTH = 190;

/** Applied via headerClass so floating-filter CSS can target all date columns. */
export const AG_GRID_DATE_COLUMN_CLASS = 'mc-ag-date-column';

/** Single-condition UTC date filter options (no And/Or). Shared across device-list date cols. */
export const AG_GRID_DATE_FILTER_OPTIONS = [
  'equals',
  'notEqual',
  'lessThan',
  'greaterThan',
  'inRange',
  'blank',
  'notBlank',
] as const;

export type AgGridDateFilterType = (typeof AG_GRID_DATE_FILTER_OPTIONS)[number];

/** Merge shared date-column width/class onto a ColDef without changing filter logic. */
export function withDateColumnLayout(colDef: ColDef): ColDef {
  return {
    ...colDef,
    // Override any flex:1 from the source colDef so date cols don't steal space.
    flex: 0,
    width: AG_GRID_DATE_COLUMN_WIDTH,
    minWidth: AG_GRID_DATE_COLUMN_WIDTH,
    maxWidth: AG_GRID_DATE_COLUMN_WIDTH + 40,
    headerClass: AG_GRID_DATE_COLUMN_CLASS,
    // Do not set suppressFloatingFilterButton here — leave undefined so AG Grid
    // shows the funnel (false can lose to defaultColDef merges in some cases).
  };
}

/**
 * Shared AG Grid date filter params — same operators / single-condition UX
 * for every UTC date column.
 */
export function createUtcDateFilterParams(
  overrides: Record<string, unknown> = {}
): IDateFilterParams {
  return {
    browserDatePicker: true,
    filterOptions: [...AG_GRID_DATE_FILTER_OPTIONS],
    defaultOption: 'equals',
    // Single condition only (replaces legacy suppressAndOrCondition)
    maxNumConditions: 1,
    maxValidYear: moment.utc().year(),
    minValidYear: 2000,
    buttons: ['reset', 'apply'],
    inRangeInclusive: true,
    comparator: (filterLocalDateAtMidnight: Date, cellValue: string) => {
      if (!cellValue) return -1;

      // Cell values are UTC timestamps; filter Date is local calendar midnight.
      const cellDate = moment.utc(cellValue).format('YYYY-MM-DD');
      const filterDate = moment(filterLocalDateAtMidnight).format('YYYY-MM-DD');

      if (cellDate < filterDate) return -1;
      if (cellDate > filterDate) return 1;
      return 0;
    },
    ...overrides,
  } as IDateFilterParams;
}

/** Format a cell Instant/LocalDateTime string as UTC wall-clock for the grid. */
export function formatUtcDateTimeCell(value: unknown): string {
  if (value == null || value === '') {
    return 'N/A';
  }
  const m = moment.utc(value as string | Date);
  return m.isValid() ? m.format('YYYY-MM-DD HH:mm:ss') : 'N/A';
}

/**
 * AG Grid dateFrom/dateTo is typically "YYYY-MM-DD HH:mm:ss" for the picked
 * calendar day. Take the calendar date only — do not shift via timezone.
 */
export function toApiDateOnly(dateFromOrTo: string): string {
  if (!dateFromOrTo) {
    return '';
  }
  const trimmed = String(dateFromOrTo).trim();
  const datePart = trimmed.length >= 10 ? trimmed.substring(0, 10) : trimmed;
  const parsed = moment(datePart, 'YYYY-MM-DD', true);
  if (parsed.isValid()) {
    return parsed.format('YYYY-MM-DD');
  }
  const fallback = moment.utc(trimmed);
  return fallback.isValid() ? fallback.format('YYYY-MM-DD') : '';
}

/** Map AG Grid date filter type → API operator string. */
export function mapAgGridDateOperator(type: string): string {
  switch (type) {
    case 'equals':
      return 'equals';
    case 'notEqual':
      return 'notEqual';
    case 'lessThan':
      return 'lessThan';
    case 'greaterThan':
      return 'greaterThan';
    case 'inRange':
      return 'between';
    case 'blank':
      return 'blank';
    case 'notBlank':
      return 'notBlank';
    default:
      return 'equals';
  }
}

export type ApiDateFilter = {
  field: string;
  operator: string;
  value: unknown;
};

/** Map one AG Grid filter model entry to the device-search API filter. */
export function mapAgGridFilterToApi(
  field: string,
  filter: Record<string, unknown> | null | undefined
): ApiDateFilter | null {
  if (!filter) {
    return null;
  }

  const type = String(filter['type'] ?? 'equals');

  if (type === 'blank') {
    return { field, operator: 'blank', value: null };
  }
  if (type === 'notBlank') {
    return { field, operator: 'notBlank', value: null };
  }

  const dateFrom = filter['dateFrom'] as string | undefined;
  const dateTo = filter['dateTo'] as string | undefined;
  const isDateFilter =
    !!dateFrom || filter['filterType'] === 'date' || type === 'inRange';

  if (isDateFilter) {
    const operator = mapAgGridDateOperator(type);

    if (type === 'inRange') {
      if (!dateFrom || !dateTo) {
        return null;
      }
      const from = toApiDateOnly(dateFrom);
      const to = toApiDateOnly(dateTo);
      if (!from || !to) {
        return null;
      }
      return { field, operator: 'between', value: [from, to] };
    }

    if (!dateFrom) {
      return null;
    }
    const value = toApiDateOnly(dateFrom);
    if (!value) {
      return null;
    }
    return { field, operator, value };
  }

  const textValue = filter['filter'];
  if (textValue !== undefined && textValue !== null && textValue !== '') {
    return {
      field,
      operator: 'like',
      value: textValue,
    };
  }

  return null;
}
